import { useMemo, useState, useCallback, memo, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ColumnOption } from "@/types";
import Metrics from "@/components/common/metrics";
import { CreativeReportRow, CreativeReportColumn } from "../types";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import { SqButton } from "@/components/squircle-ui/button";
import { RiArrowDownSLine, RiBarChart2Fill } from "@remixicon/react";
import { FooterInfo } from "./FooterInfo";
// Removed getMetricFormulaLabel import - formula not needed
// Removed unused imports - Y axis is hidden
import { ChartTooltip } from "@/components/ui/tremor-charts/components/ChartTooltip";
import {
  getColorClassName,
  constructCategoryColors,
  getColorHex,
  calculateNiceYAxisDomain,
  generateCustomTicks,
} from "@/lib/chartUtils";
import { cn } from "@/lib/utils";
import { useGlobalStore } from "@/GlobalStore";
import { PRIMARY_COLUMN_JOINER } from "../constants";
import AdCard from "@/components/common/adCard";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ReportLoader from "./ReportLoader";

interface CreativePerformanceBarChartProps {
  rows: CreativeReportRow[];
  columns: CreativeReportColumn[];
  availableMetrics: ColumnOption[];
  initialConfig?: {
    metric?: string | null; // Deprecated: use metrics instead
    metrics?: string[]; // Array of metric keys
  };
  onConfigChange?: (config: { metric?: string | null; metrics?: string[] }) => void;
  metricOptions: ColumnOption[];
  sectionId?: string;
  groupByKey?: string;
  barChartPageSize?: number;
}

const MAX_METRICS = 3;
const BAR_COLORS = ["turquoiseLight", "magenta", "lavender"] as const;

export const CreativePerformanceBarChart = memo(function CreativePerformanceBarChart({
  rows,
  columns,
  availableMetrics,
  initialConfig,
  onConfigChange,
  metricOptions,
  sectionId,
  groupByKey,
}: CreativePerformanceBarChartProps) {
  // Get visible ad names from global store (same as line chart)
  const visibleAdNames = useGlobalStore(
    (state) => state.visibleAdNamesBySection[sectionId || ""] || [],
  );
  const FOOTER_DISMISSED_KEY = "creative-performance-bar-chart-footer-dismissed";
  const COMPARE_DATE_FOOTER_DISMISSED_KEY =
    "creative-performance-bar-chart-compare-date-footer-dismissed";

  // State for creative preview
  const [previewCreative, setPreviewCreative] = useState<
    | {
      creative: unknown;
      adName: string;
    }
    | undefined
  >();

  // Get all primary column keys
  const primaryColumnKeys = useMemo(
    () => columns.filter((col) => col.is_primary).map((col) => col.key),
    [columns],
  );

  // Detect breakdown category key from rows (e.g., "gender")
  // This is a key that appears in breakdowns but represents the breakdown category
  const breakdownCategoryKey = useMemo(() => {
    // Find first row with breakdowns
    const rowWithBreakdowns = rows.find(
      (row) =>
        row.breakdowns && Array.isArray(row.breakdowns) && row.breakdowns.length > 0,
    );
    if (!rowWithBreakdowns?.breakdowns?.[0]) return null;

    const firstBreakdown = rowWithBreakdowns.breakdowns[0];
    const mainRowKeys = new Set(Object.keys(rowWithBreakdowns));

    // Find a key in breakdown that is a string and not in main row (or is different type)
    // This should be the breakdown category key (e.g., "gender")
    for (const key in firstBreakdown) {
      const value = firstBreakdown[key];
      // Skip if it's a metric (number) or if it's in main row with same type
      if (typeof value === "string" && value !== "" && !mainRowKeys.has(key)) {
        return key;
      }
      // Also check if it exists in main row but as different type (e.g., main row has it as null/undefined)
      if (typeof value === "string" && value !== "") {
        const mainValue = (rowWithBreakdowns as Record<string, unknown>)[key];
        if (mainValue === null || mainValue === undefined || mainValue === "") {
          return key;
        }
      }
    }
    return null;
  }, [rows]);

  // Detect if compare date mode is enabled by checking for _previous keys
  const isCompareDateEnabled = useMemo(() => {
    if (!rows || rows.length === 0) return false;
    // Check if any row has _previous keys for any metric
    const firstRow = rows[0] as Record<string, unknown>;
    return Object.keys(firstRow).some((key) => key.endsWith("_previous"));
  }, [rows]);
  const [selectedMetrics, setSelectedMetrics] = useState<ColumnOption[]>(() => {
    // Support new multi-metric format first
    if (initialConfig?.metrics && initialConfig.metrics.length > 0) {
      const metrics = initialConfig.metrics
        .map((key) => availableMetrics.find((m) => m.key === key))
        .filter((m): m is ColumnOption => m !== undefined);
      if (metrics.length > 0) return metrics;
    }
    // Fallback to old single metric format for backward compatibility
    if (
      initialConfig?.metric &&
      availableMetrics.some((metric) => metric.key === initialConfig.metric)
    ) {
      const metric = availableMetrics.find((m) => m.key === initialConfig.metric);
      return metric ? [metric] : [];
    }
    // Fall back to first two available metrics when possible
    return availableMetrics.slice(0, 2);
  });

  // Store onConfigChange in ref to avoid infinite loops
  const onConfigChangeRef = useRef(onConfigChange);
  useEffect(() => {
    onConfigChangeRef.current = onConfigChange;
  }, [onConfigChange]);

  // Automatically limit to 1 metric when compare mode is enabled
  useEffect(() => {
    if (isCompareDateEnabled && selectedMetrics.length > 1) {
      const firstMetric = selectedMetrics[0];
      setSelectedMetrics([firstMetric]);
      onConfigChangeRef.current?.({
        metrics: [firstMetric?.key || ""].filter(Boolean),
        metric: firstMetric?.key || null, // Keep for backward compatibility
      });
    }
  }, [isCompareDateEnabled, selectedMetrics]);

  // Handle case when selected metrics are no longer in available metrics
  useEffect(() => {
    if (availableMetrics.length === 0) {
      // No available metrics, clear selection
      if (selectedMetrics.length > 0) {
        setSelectedMetrics([]);
        onConfigChangeRef.current?.({ metrics: [], metric: null });
      }
      return;
    }

    // Filter out only the metrics that are no longer available
    const validMetrics = selectedMetrics.filter((selected) =>
      availableMetrics.some((available) => available.key === selected.key),
    );

    // Only update if some metrics were removed
    if (validMetrics.length !== selectedMetrics.length) {
      if (validMetrics.length > 0) {
        // Keep the valid metrics
        setSelectedMetrics(validMetrics);
        const metricKeys = validMetrics.map((m) => m.key);
        onConfigChangeRef.current?.({
          metrics: metricKeys,
          metric: validMetrics[0]?.key || null, // Keep for backward compatibility
        });
      } else {
        // All metrics became invalid, fall back to first available
        const firstAvailable = availableMetrics[0];
        if (firstAvailable) {
          setSelectedMetrics([firstAvailable]);
          onConfigChangeRef.current?.({
            metrics: [firstAvailable.key || ""].filter(Boolean),
            metric: firstAvailable.key || null, // Keep for backward compatibility
          });
        } else {
          setSelectedMetrics([]);
          onConfigChangeRef.current?.({ metrics: [], metric: null });
        }
      }
    }
  }, [availableMetrics, selectedMetrics]);

  // Update configuration when selection changes
  const handleMetricChange = useCallback(
    (metrics: ColumnOption[]) => {
      // When compare date is enabled, limit to 1 metric
      const maxMetrics = isCompareDateEnabled ? 1 : MAX_METRICS;
      const limitedMetrics = metrics.slice(0, maxMetrics);
      setSelectedMetrics(limitedMetrics);
      // Store metrics array and also first metric for backward compatibility
      const metricKeys = limitedMetrics.map((m) => m.key);
      onConfigChange?.({
        metrics: metricKeys,
        metric: limitedMetrics[0]?.key || null, // Keep for backward compatibility
      });

      // Show warning if user tried to select more than allowed
      if (metrics.length > maxMetrics) {
        const message = isCompareDateEnabled
          ? "Only one metric is allowed when compare date is enabled."
          : `Maximum ${MAX_METRICS} metrics allowed. Only the first ${MAX_METRICS} were selected.`;
        console.warn(message);
      }
    },
    [onConfigChange, isCompareDateEnabled],
  );

  const getSelectedMetrics = useMemo(() => {
    return selectedMetrics;
  }, [selectedMetrics]);

  // Get color for a metric based on its index in selectedMetrics
  const getMetricColor = useCallback((metric: ColumnOption, index: number): string => {
    const colorName = BAR_COLORS[index % BAR_COLORS.length];
    return getColorHex(colorName);
  }, []);

  // Function to determine if a metric should be disabled
  // Disable metrics when 3 are already selected (and not in compare date mode)
  const isMetricDisabled = useCallback(
    (metric: ColumnOption): boolean => {
      // In normal mode, disable when 3 metrics are selected and this metric is not one of them
      return (
        selectedMetrics.length >= MAX_METRICS &&
        !selectedMetrics.some((m) => m.key === metric.key)
      );
    },
    [selectedMetrics],
  );

  // Prepare chart data from rows, filtered by visible ad names (same as line chart)
  const chartData = useMemo(() => {
    if (!selectedMetrics.length || !rows || rows.length === 0) return [];

    // Find the primary column for the index (creative name/image)
    const primaryColumn = columns.find((c) => c.is_primary) || columns[0];
    if (!primaryColumn) return [];
    // If no visible ad names, show all rows (fallback behavior)
    const hasVisibleAdNames = visibleAdNames.length > 0;
    const selectedMetricKeys = selectedMetrics.map((m) => m.key);

    // Create a Map of fullName -> index for efficient lookup and sorting
    const visibleAdNamesMap = new Map<string, number>();
    if (hasVisibleAdNames) {
      visibleAdNames.forEach((adName, index) => {
        visibleAdNamesMap.set(adName.fullName, index);
      });
    } else {
      return [];
    }

    // Helper function to get fullName from a row
    const getFullName = (row: CreativeReportRow): string => {
      const primaryValues = primaryColumnKeys.map((key) => {
        const value = (row as Record<string, unknown>)[key];
        if (value === null || value === undefined) return "";
        if (typeof value === "string" || typeof value === "number") {
          return String(value);
        }
        return "";
      });
      return primaryValues.join(PRIMARY_COLUMN_JOINER);
    };

    return rows
      .filter((row) => {
        // Check if row has breakdowns
        const hasBreakdowns =
          row.breakdowns &&
          Array.isArray(row.breakdowns) &&
          row.breakdowns.length > 0 &&
          breakdownCategoryKey;

        if (hasBreakdowns && row.breakdowns) {
          // Check if any breakdown has at least one of the selected metrics
          const hasAtLeastOneMetric = row.breakdowns.some(
            (breakdown: Record<string, unknown>) =>
              selectedMetricKeys.some((metricKey) => {
                const metricValue = breakdown[metricKey];
                if (isCompareDateEnabled) {
                  // In compare mode, also check for previous values in breakdowns
                  const previousValue = breakdown[`${metricKey}_previous`];
                  return (
                    (metricValue !== undefined && metricValue !== null) ||
                    (previousValue !== undefined && previousValue !== null)
                  );
                }
                return metricValue !== undefined && metricValue !== null;
              }),
          );
          if (!hasAtLeastOneMetric) return false;
        } else {
          // Filter out rows that don't have at least one of the selected metrics
          // In compare mode, also check for previous values
          const hasAtLeastOneMetric = selectedMetricKeys.some((metricKey) => {
            const metricValue = (row as Record<string, unknown>)[metricKey];
            if (isCompareDateEnabled) {
              // In compare mode, need both current and previous values
              const previousValue = (row as Record<string, unknown>)[
                `${metricKey}_previous`
              ];
              return (
                (metricValue !== undefined && metricValue !== null) ||
                (previousValue !== undefined && previousValue !== null)
              );
            }
            return metricValue !== undefined && metricValue !== null;
          });
          if (!hasAtLeastOneMetric) return false;
        }

        // If we have visible ad names, filter by them using the Map
        if (hasVisibleAdNames) {
          const fullName = getFullName(row);
          return visibleAdNamesMap.has(fullName);
        }

        return true;
      })
      .map((row) => {
        const primaryValue = (row as Record<string, unknown>)[primaryColumn.key];

        const adForThumb = row.ads?.[0] ?? row.first_ad_metadata;
        const thumbnailUrl =
          row.creative?.thumbnail_url ||
          adForThumb?.creative?.thumbnail_url ||
          adForThumb?.creative?.images?.[0]?.permalink_url ||
          adForThumb?.creative?.videos?.[0]?.thumbnail_url ||
          row.creative_thumbnail ||
          "";

        // Convert primaryValue to string safely
        const indexValue =
          typeof primaryValue === "string"
            ? primaryValue
            : typeof primaryValue === "number"
              ? String(primaryValue)
              : "";

        // Check if this row has breakdowns
        const hasBreakdowns =
          row.breakdowns &&
          Array.isArray(row.breakdowns) &&
          row.breakdowns.length > 0 &&
          breakdownCategoryKey;

        // Include all selected metrics in the data
        const dataPoint: Record<string, string | number> = {
          index: indexValue,
        };

        if (hasBreakdowns && row.breakdowns) {
          // Use breakdowns - create stacked bar segments
          // For each breakdown, create a property: {metricKey}_{breakdownValue} for current
          // and {metricKey}_previous_{breakdownValue} for previous (when compare mode is enabled)
          row.breakdowns.forEach((breakdown: Record<string, unknown>) => {
            const breakdownValue = breakdown[breakdownCategoryKey];
            const breakdownValueStr =
              typeof breakdownValue === "string"
                ? breakdownValue
                : typeof breakdownValue === "number"
                  ? String(breakdownValue)
                  : "";

            if (breakdownValueStr) {
              // Add each selected metric value for this breakdown segment
              selectedMetricKeys.forEach((metricKey) => {
                const metricValue = breakdown[metricKey];
                const segmentKey = `${metricKey}_${breakdownValueStr}`;
                dataPoint[segmentKey] = typeof metricValue === "number" ? metricValue : 0;

                // If compare date is enabled, add previous value for this breakdown segment
                if (isCompareDateEnabled) {
                  const previousValue = breakdown[`${metricKey}_previous`];
                  const previousSegmentKey = `${metricKey}_previous_${breakdownValueStr}`;
                  dataPoint[previousSegmentKey] =
                    typeof previousValue === "number" ? previousValue : 0;
                }
              });
            }
          });
        } else {
          // No breakdowns - use main row values
          selectedMetricKeys.forEach((metricKey) => {
            const metricValue = (row as Record<string, unknown>)[metricKey];
            dataPoint[metricKey] = typeof metricValue === "number" ? metricValue : 0;

            // If compare date is enabled, add previous value
            if (isCompareDateEnabled) {
              const previousValue = (row as Record<string, unknown>)[
                `${metricKey}_previous`
              ];
              dataPoint[`${metricKey}_previous`] =
                typeof previousValue === "number" ? previousValue : 0;
            }
          });
        }

        // Store thumbnailUrl and creative separately for CustomTick
        (dataPoint as Record<string, unknown>).thumbnailUrl = thumbnailUrl;
        (dataPoint as Record<string, unknown>).creative =
          row.creative ||
          row.ads?.[0]?.creative ||
          (row.creative_thumbnail
            ? {
              images: [
                { permalink_url: row.creative_thumbnail, url: row.creative_thumbnail },
              ],
              videos: [],
              thumbnail_url: row.creative_thumbnail,
            }
            : undefined);

        // Store fullName and sortIndex for sorting
        const fullName = getFullName(row);
        (dataPoint as Record<string, unknown>).fullName = fullName;
        // Store sort index from Map (or Infinity if not found, so they sort last)
        const sortIndex = visibleAdNamesMap.get(fullName) ?? Infinity;
        (dataPoint as Record<string, unknown>).sortIndex = sortIndex;

        return dataPoint;
      })
      .filter((item) => item.index) // Remove items without index
      .sort((a, b) => {
        // If visibleAdNames is present, sort by the stored sortIndex
        if (hasVisibleAdNames) {
          const aIndex = (a as Record<string, unknown>).sortIndex as number;
          const bIndex = (b as Record<string, unknown>).sortIndex as number;
          return aIndex - bIndex;
        }
        // If no visibleAdNames, maintain original order
        return 0;
      });
  }, [
    rows,
    columns,
    selectedMetrics,
    visibleAdNames,
    primaryColumnKeys,
    breakdownCategoryKey,
    isCompareDateEnabled,
  ]);

  // Format number with abbreviations (15.2k, 1.2M) - max 3 digits before decimal, 4 digits total including decimal
  const formatNumberCompact = useCallback((num: number): string => {
    if (num >= 1000000) {
      const millions = num / 1000000;
      const wholePart = Math.floor(millions);
      // If whole part is 3 digits (>= 100), round to whole number (no decimal)
      if (wholePart >= 100) {
        return Math.round(millions).toFixed(0);
      }
      // For 1-2 digit whole parts, allow 1 decimal (max 4 digits total: e.g., 12.3M, 1.2M)
      return millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1);
    } else if (num >= 1000) {
      const thousands = num / 1000;
      const wholePart = Math.floor(thousands);
      // If whole part is 3 digits (>= 100), round to whole number (no decimal)
      // Otherwise allow 1 decimal (e.g., 78.6k is fine, but 693.7k becomes 694k)
      if (wholePart >= 100) {
        return Math.round(thousands).toFixed(0);
      }
      return thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1);
    }
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
  }, []);

  // Get abbreviation suffix (K, M)
  const getNumberSuffix = useCallback((num: number): string => {
    if (num >= 1000000) return "M";
    if (num >= 1000) return "k";
    return "";
  }, []);

  // Get value formatter for a specific metric (with prefix, suffix, and number formatting)
  const getValueFormatter = useCallback(
    (metricKey: string) => {
      const metricOption = metricOptions.find((m) => m.key === metricKey);
      if (!metricOption) return (value: number) => value.toString();

      const prefix = metricOption.prefix || "";
      const suffix = metricOption.suffix || "";

      return (value: number) => {
        const absValue = Math.abs(value);
        const formattedNumber = formatNumberCompact(absValue);
        const numberSuffix = getNumberSuffix(absValue);
        const sign = value < 0 ? "-" : "";
        return `${sign}${prefix}${formattedNumber}${numberSuffix}${suffix}`;
      };
    },
    [metricOptions, formatNumberCompact, getNumberSuffix],
  );

  // Value formatter for tooltip (formats each metric with its own formatter)
  const valueFormatter = useCallback(
    (value: number, payloadItem?: { metricKey?: string }) => {
      const metricKey = payloadItem?.metricKey;
      if (!metricKey) {
        // Fallback to first metric formatter when metricKey not provided (e.g. other chart types)
        if (!selectedMetrics.length) return value.toString();
        return getValueFormatter(selectedMetrics[0].key)(value);
      }
      // Use formatter for the specific metric (correct prefix/suffix per metric)
      return getValueFormatter(metricKey)(value);
    },
    [selectedMetrics, getValueFormatter],
  );

  // Helper function to wrap text into 2 lines with truncation
  const wrapText = useCallback((text: string, maxWidth: number = 100): string[] => {
    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";
    const characterWidth = 7;
    const maxCharsPerLine = Math.floor(maxWidth / characterWidth);
    const ellipsisLength = 3; // "..."
    const maxCharsWithEllipsis = maxCharsPerLine - ellipsisLength;

    // Helper to truncate a line and add ellipsis
    const truncateLine = (line: string): string => {
      if (line.length <= maxCharsPerLine) return line;
      return line.substring(0, Math.max(0, maxCharsWithEllipsis)) + "...";
    };

    for (const word of words) {
      // Stop if we already have 2 lines
      if (lines.length >= 2) {
        break;
      }

      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testLineLength = testLine.length;

      // Check if it fits based on character count
      if (testLineLength <= maxCharsPerLine) {
        // Fits on current line
        currentLine = testLine;
      } else {
        // Doesn't fit - need to move to next line or truncate
        if (lines.length === 0) {
          // We're building the first line
          if (currentLine) {
            // Push the current line (it fits since we got here)
            lines.push(currentLine);
            // Start new line with the word
            currentLine = word;
            // Check if the word alone is too long for the second line
            if (word.length > maxCharsPerLine) {
              // Truncate it immediately
              lines.push(truncateLine(word));
              currentLine = "";
              break;
            }
          } else {
            // First word is too long - truncate it
            lines.push(truncateLine(word));
            currentLine = "";
            break;
          }
        } else if (lines.length === 1) {
          // We're building the second line - must truncate if needed
          const lineToAdd = currentLine ? `${currentLine} ${word}` : word;
          lines.push(truncateLine(lineToAdd));
          currentLine = "";
          break; // Never add more than 2 lines
        }
      }
    }

    // After processing all words, add remaining currentLine if we have space
    if (currentLine && lines.length < 2) {
      lines.push(truncateLine(currentLine));
    }

    // Ensure we always return at least one line
    if (lines.length === 0) {
      return [truncateLine(text)];
    }

    // Ensure we never return more than 2 lines
    return lines.slice(0, 2);
  }, []);

  // Custom tick component to show creative images or text based on group_by
  interface TickProps {
    x?: number;
    y?: number;
    payload?: {
      value: string | number;
    };
  }

  const CustomTick = useCallback(
    ({ x, y, payload }: TickProps) => {
      const indexValue = String(payload?.value || "");
      const dataPoint = chartData.find((d) => d.index === indexValue);
      const thumbnailUrl = (dataPoint as Record<string, unknown>)?.thumbnailUrl as
        | string
        | undefined;
      const creative = (dataPoint as Record<string, unknown>)?.creative;

      const isCreativeName = groupByKey === "creative_name";
      const hasCreative = !!creative;

      const handleThumbnailClick = (e: React.MouseEvent<SVGGElement>) => {
        e.stopPropagation();
        if (creative) {
          setPreviewCreative({
            creative,
            adName: indexValue,
          });
        }
      };

      return (
        <g transform={`translate(${x},${y})`}>
          {isCreativeName ? (
            // Show only image when group_by is "creative_name"
            thumbnailUrl && typeof thumbnailUrl === "string" ? (
              <g>
                <defs>
                  <clipPath id={`image-clip-${indexValue.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                    <rect x={-30} y={0} width={60} height={60} rx={8} ry={8} />
                  </clipPath>
                </defs>
                <g
                  style={{ cursor: hasCreative ? "pointer" : "default" }}
                  onClick={hasCreative ? handleThumbnailClick : undefined}
                  onMouseEnter={(e) => {
                    if (hasCreative) {
                      e.currentTarget.style.opacity = "0.8";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (hasCreative) {
                      e.currentTarget.style.opacity = "1";
                    }
                  }}
                >
                  <image
                    href={thumbnailUrl}
                    x={-30}
                    y={0}
                    width={60}
                    height={60}
                    preserveAspectRatio="xMidYMid slice"
                    clipPath={`url(#image-clip-${indexValue.replace(/[^a-zA-Z0-9]/g, "-")})`}
                  />
                </g>
              </g>
            ) : null
          ) : (
            // Show only text when group_by is not "creative_name" - wrapped to 2 lines
            <text
              x={0}
              y={10}
              dy={16}
              textAnchor="middle"
              fill="#021859CC"
              fontSize={12}
              className="fill-brand-primary-800"
            >
              {wrapText(indexValue).map((line, index) => (
                <tspan key={index} x={0} dy={index === 0 ? 0 : 14} textAnchor="middle">
                  {line}
                </tspan>
              ))}
            </text>
          )}
        </g>
      );
    },
    [chartData, groupByKey, wrapText],
  );

  // Label formatter factory for values on top of bars (with prefix, suffix, and number formatting)
  // Recharts label formatter receives (value, name, props)
  const getLabelFormatter = useCallback(
    (metricKey: string) => {
      const metricOption = metricOptions.find((m) => m.key === metricKey);
      if (!metricOption) return () => "";

      const prefix = metricOption.prefix || "";
      const suffix = metricOption.suffix || "";

      return (_value: number | string) => {
        const numValue = typeof _value === "number" ? _value : Number(_value);
        if (!numValue || numValue === 0 || isNaN(numValue)) {
          return "";
        }
        // Format with prefix, suffix, and number abbreviations
        const absValue = Math.abs(numValue);
        const formattedNumber = formatNumberCompact(absValue);
        const numberSuffix = getNumberSuffix(absValue);
        const sign = numValue < 0 ? "-" : "";
        return `${sign}${prefix}${formattedNumber}${numberSuffix}${suffix}`;
      };
    },
    [metricOptions, formatNumberCompact, getNumberSuffix],
  );

  // Get all unique breakdown values from chartData (if using breakdowns)
  const breakdownValues = useMemo(() => {
    if (!breakdownCategoryKey || !chartData.length) return [];
    const values = new Set<string>();
    chartData.forEach((dataPoint) => {
      Object.keys(dataPoint).forEach((key) => {
        // Check if key matches pattern: {metricKey}_{breakdownValue} or {metricKey}_previous_{breakdownValue}
        if (key.includes("_") && key !== "index" && !key.endsWith("_previous")) {
          const parts = key.split("_");
          if (parts.length >= 2) {
            // For compare mode with breakdowns: {metricKey}_previous_{breakdownValue}
            // For regular breakdowns: {metricKey}_{breakdownValue}
            if (isCompareDateEnabled && parts.length >= 3 && parts[1] === "previous") {
              // Extract breakdown value from {metricKey}_previous_{breakdownValue}
              const breakdownValue = parts.slice(2).join("_");
              values.add(breakdownValue);
            } else if (!isCompareDateEnabled) {
              // Extract breakdown value from {metricKey}_{breakdownValue}
              const breakdownValue = parts.slice(1).join("_");
              values.add(breakdownValue);
            }
          }
        }
      });
    });
    return Array.from(values).sort();
  }, [chartData, breakdownCategoryKey, isCompareDateEnabled]);

  // Check if we're using breakdowns (has breakdown values)
  const isUsingBreakdowns = breakdownValues.length > 0;

  // Calculate independent Y-axis domain for each metric
  // This ensures each metric has its own scale, making bars visible even with vastly different values
  const metricDomains = useMemo(() => {
    if (!selectedMetrics.length || !chartData.length)
      return new Map<string, [number, number]>();

    const domains = new Map<string, [number, number]>();

    selectedMetrics.forEach((metric) => {
      const metricKey = metric.key;
      let dataMaxValue = -Infinity;

      if (isUsingBreakdowns) {
        // For breakdowns, calculate the sum of all segments for each data point
        // The domain should be based on the total height of stacked bars, not individual segments
        chartData.forEach((dataPoint) => {
          let totalCurrent = 0;
          let totalPrevious = 0;

          breakdownValues.forEach((breakdownValue) => {
            // Sum current breakdown segments
            const currentKey = `${metricKey}_${breakdownValue}`;
            const currentValue = Number(dataPoint[currentKey]) || 0;
            totalCurrent += currentValue;

            // Sum previous breakdown segments if compare mode is enabled
            if (isCompareDateEnabled) {
              const previousKey = `${metricKey}_previous_${breakdownValue}`;
              const previousValue = Number(dataPoint[previousKey]) || 0;
              totalPrevious += previousValue;
            }
          });

          // Use the maximum of current and previous totals
          const maxTotal = isCompareDateEnabled
            ? Math.max(totalCurrent, totalPrevious)
            : totalCurrent;
          dataMaxValue = Math.max(dataMaxValue, maxTotal);
        });
      } else {
        // For non-breakdown charts, use the metric key directly
        const categories: string[] = [metricKey];
        if (isCompareDateEnabled) {
          categories.push(`${metricKey}_previous`);
        }

        // Use existing calculateNiceYAxisDomain for non-breakdown charts
        const domain = calculateNiceYAxisDomain(
          chartData,
          categories,
          true,
          undefined,
          undefined,
          5,
        ) as [number, number];
        domains.set(metricKey, domain);
        return; // Skip to next metric
      }

      // Calculate domain from the max value for breakdowns
      if (dataMaxValue === -Infinity || dataMaxValue === 0) {
        domains.set(metricKey, [0, 8]);
        return;
      }

      // Reuse existing domain calculation logic
      const domain = calculateNiceYAxisDomain(
        [{ value: dataMaxValue }],
        ["value"],
        true,
        undefined,
        undefined,
        5,
      ) as [number, number];
      domains.set(metricKey, domain);
    });

    return domains;
  }, [
    selectedMetrics,
    chartData,
    isUsingBreakdowns,
    breakdownValues,
    isCompareDateEnabled,
  ]);

  // Calculate custom ticks for each metric domain to ensure exactly 5 evenly spaced grid lines
  const metricTicks = useMemo(() => {
    if (!selectedMetrics.length) return new Map<string, number[]>();
    const ticksMap = new Map<string, number[]>();
    selectedMetrics.forEach((metric) => {
      const domain = metricDomains.get(metric.key) || [0, 100];
      const ticks = generateCustomTicks(domain, 5, true);
      ticksMap.set(metric.key, ticks);
    });
    return ticksMap;
  }, [selectedMetrics, metricDomains]);

  // Category colors for all selected metrics and breakdown segments
  const categoryColors = useMemo(() => {
    if (!selectedMetrics.length) return new Map();

    if (isUsingBreakdowns) {
      // For breakdowns, use same base color for each metric with varying opacity
      // Opacity ranges from 100% (first breakdown) to 25% (last breakdown)
      const colorMap = new Map<string, { color: string; opacity: number }>();
      const numBreakdowns = breakdownValues.length;

      selectedMetrics.forEach((metric) => {
        if (isCompareDateEnabled) {
          // For compare mode with breakdowns:
          // - Current bars use magenta (BAR_COLORS[1]) with varying opacity
          // - Previous bars use turquoiseLight (BAR_COLORS[0]) with varying opacity
          breakdownValues.forEach((breakdownValue, breakdownIndex) => {
            // Current breakdown segment
            const currentSegmentKey = `${metric.key}_${breakdownValue}`;
            const currentOpacity =
              numBreakdowns === 1
                ? 1.0
                : 1.0 - (0.75 * breakdownIndex) / (numBreakdowns - 1);
            colorMap.set(currentSegmentKey, {
              color: BAR_COLORS[1], // magenta for current
              opacity: currentOpacity,
            });

            // Previous breakdown segment
            const previousSegmentKey = `${metric.key}_previous_${breakdownValue}`;
            const previousOpacity =
              numBreakdowns === 1
                ? 1.0
                : 1.0 - (0.75 * breakdownIndex) / (numBreakdowns - 1);
            colorMap.set(previousSegmentKey, {
              color: BAR_COLORS[0], // turquoiseLight for previous
              opacity: previousOpacity,
            });
          });
        } else {
          // Regular breakdown mode
          const metricIndex = selectedMetrics.indexOf(metric);
          const baseColor = BAR_COLORS[metricIndex % BAR_COLORS.length];

          breakdownValues.forEach((breakdownValue, breakdownIndex) => {
            const segmentKey = `${metric.key}_${breakdownValue}`;
            // Calculate opacity: 100% for first, 25% for last, evenly distributed
            const opacity =
              numBreakdowns === 1
                ? 1.0
                : 1.0 - (0.75 * breakdownIndex) / (numBreakdowns - 1);
            colorMap.set(segmentKey, { color: baseColor, opacity });
          });
        }
      });
      return colorMap;
    } else {
      // Original behavior for non-breakdown charts
      const metricKeys = selectedMetrics.map((m) => m.key);
      const colors = BAR_COLORS.slice(0, selectedMetrics.length);
      return constructCategoryColors(metricKeys, colors);
    }
  }, [selectedMetrics, isUsingBreakdowns, breakdownValues, isCompareDateEnabled]);

  const noMetric = !selectedMetrics.length;
  const noData = chartData.length === 0;

  // Bar size: 30 for 1 bar, 25 for 2 bars, 20 for 3+ bars per category
  const barsPerCategory = isCompareDateEnabled ? 2 : selectedMetrics.length;
  const chartBarSize =
    barsPerCategory === 1 ? 30 : barsPerCategory === 2 ? 25 : 20;

  return (
    <Card className="mb-2 rounded-[12px] shadow-none pt-[13px] px-3 pb-2 overflow-visible">
      <CardHeader className="p-0 space-y-0  flex flex-row items-center justify-between">
        <div className="flex flex-col">
          <CardTitle>
            <div className="flex items-center gap-2">
              <RiBarChart2Fill size={16} className="text-brand-primary-600 shrink-0" />
              <h4 className="text-brand-primary-800 overflow-hidden text-ellipsis relative top-[1px] text-[13px] font-medium not-italic leading-none">
                Bar chart
              </h4>
            </div>
          </CardTitle>
        </div>
        <div className="flex gap-2">
          <Metrics
            renderingStyle={isCompareDateEnabled ? "single_select" : "multiselect"}
            selectedMetrics={getSelectedMetrics}
            onMetricChange={handleMetricChange}
            metricsOptions={availableMetrics}
            getMetricColor={getMetricColor}
            isMetricDisabled={isMetricDisabled}
            sortAlphabetically={false}
            trigger={
              <SqTooltipProvider>
                <SqTooltip>
                  <SqTooltipTrigger>
                    <SqButton
                      className="flex items-center gap-[12px] h-7 bg-transparent pl-2.5 pr-2 py-1"
                      bgColor="#FFFFFF"
                      hasOutline
                      shadow={"sm"}
                      hoverBgColor="#FBFBFB"
                      radius={6}
                    >
                      <span className="text-brand-primary-800 text-ellipsis text-[12px] font-medium leading-[19.157px] tracking-[0.24px]">
                        Metrics{" "}
                      </span>
                      {getSelectedMetrics.length > 0 ? (
                        <div className="flex items-center gap-[12px] min-w-0 flex-1 overflow-hidden">
                          {isCompareDateEnabled && getSelectedMetrics[0] ? (
                            // Show both Current and Previous when compare mode is enabled
                            <>
                              <div className="flex items-center gap-[6px] shrink-0">
                                <span
                                  className="h-2.5 w-2.5 rounded-full shrink-0"
                                  style={{
                                    backgroundColor: getColorHex(BAR_COLORS[0]), // turquoiseLight for previous
                                  }}
                                />
                                <span className="text-brand-primary-600 text-[12px] font-normal leading-[19.157px] tracking-[0.24px] whitespace-nowrap">
                                  {getSelectedMetrics[0].label} Previous
                                </span>
                              </div>
                              <div className="flex items-center gap-[6px] shrink-0">
                                <span
                                  className="h-2.5 w-2.5 rounded-full shrink-0"
                                  style={{
                                    backgroundColor: getColorHex(BAR_COLORS[1]), // magenta for current
                                  }}
                                />
                                <span className="text-brand-primary-600 text-[12px] font-normal leading-[19.157px] tracking-[0.24px] whitespace-nowrap">
                                  {getSelectedMetrics[0].label} Current
                                </span>
                              </div>
                            </>
                          ) : (
                            // Normal mode: show selected metrics
                            getSelectedMetrics.map((metric, index) => {
                              const color = getMetricColor(metric, index);
                              return (
                                <div
                                  key={metric.key}
                                  className="flex items-center gap-[6px] shrink-0"
                                >
                                  <span
                                    className="h-2.5 w-2.5 rounded-full shrink-0"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="text-brand-primary-600 text-[12px] font-normal leading-[19.157px] tracking-[0.24px] whitespace-nowrap">
                                    {metric.label}
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      ) : null}
                      <RiArrowDownSLine size={14} color="#9aa3bd" className="shrink-0" />
                    </SqButton>
                  </SqTooltipTrigger>
                  {getSelectedMetrics[0]?.show_platforms &&
                    getSelectedMetrics[0]?.platforms &&
                    getSelectedMetrics[0]?.platforms.length > 0 && (
                      <SqTooltipContent
                        portal={true}
                        side="bottom"
                        align="start"
                        alignOffset={20}
                      >
                        <p>
                          {getSelectedMetrics[0]?.label}
                          <br />
                          Available for{" "}
                          {getSelectedMetrics[0]?.platforms
                            .map(
                              (platform) =>
                                platform.charAt(0).toUpperCase() +
                                platform.slice(1).toLowerCase(),
                            )
                            .join(", ")}
                        </p>
                      </SqTooltipContent>
                    )}
                </SqTooltip>
              </SqTooltipProvider>
            }
          />
        </div>
      </CardHeader>
      <CardContent className="p-0 mt-[24px]">
        {noMetric ? (
          <div className="h-[320px] w-full flex items-center justify-center text-gray-500">
            Select a metric
          </div>
        ) : noData ? (
          <div className="h-[320px] w-full flex items-center justify-center text-gray-500">
            <ReportLoader message="Loading chart data..." showProgressBar={true} />
          </div>
        ) : (
          <div className="h-[320px] w-full">
            <ResponsiveContainer>
              <RechartsBarChart
                data={chartData}
                margin={{
                  top: 40, // Extra space for value labels
                  right: 20,
                  bottom: groupByKey === "creative_name" ? 80 : 50, // Extra space for images (80) or 2-line text (50)
                  left: 20,
                }}
                barCategoryGap="20%"
                barGap={15}
                barSize={chartBarSize}
              >
                <CartesianGrid
                  className={cn("stroke-gray-200 stroke-1 dark:stroke-gray-800")}
                  horizontal={true}
                  vertical={false}
                  syncWithTicks
                />
                <XAxis
                  dataKey="index"
                  tick={CustomTick}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={10}
                  interval={0}
                  className={cn("text-xs fill-gray-500 dark:fill-gray-500")}
                />
                {/* Create independent Y-axes for each metric (hidden) */}
                {selectedMetrics.map((metric) => {
                  const domain = metricDomains.get(metric.key) || [0, 100];
                  const ticks = metricTicks.get(metric.key) || [];
                  return (
                    <YAxis
                      key={`yaxis-${metric.key}`}
                      yAxisId={`yaxis-${metric.key}`}
                      hide={true}
                      domain={domain}
                      ticks={ticks}
                    />
                  );
                })}
                <Tooltip
                  wrapperStyle={{ outline: "none", zIndex: 50 }}
                  isAnimationActive={true}
                  animationDuration={100}
                  cursor={{ fill: "#d1d5db", opacity: "0.15" }}
                  offset={20}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) return null;

                    interface RechartsTooltipPayloadItem {
                      dataKey?: string | number;
                      value?: number | string;
                      payload?: Record<string, unknown>;
                      type?: string;
                    }

                    const cleanPayload = payload.map((item: unknown) => {
                      const payloadItem = item as RechartsTooltipPayloadItem;
                      const dataKey = String(payloadItem.dataKey ?? "");
                      let metricKey = dataKey;
                      let breakdownValue: string | undefined;
                      let isPrevious = false;

                      // Check if this is a breakdown segment with compare mode
                      // Format: {metricKey}_previous_{breakdownValue} or {metricKey}_{breakdownValue}
                      if (
                        isCompareDateEnabled &&
                        isUsingBreakdowns &&
                        dataKey.includes("_previous_")
                      ) {
                        const parts = dataKey.split("_previous_");
                        if (parts.length === 2) {
                          metricKey = parts[0];
                          breakdownValue = parts[1];
                          isPrevious = true;
                        }
                      } else if (
                        isCompareDateEnabled &&
                        isUsingBreakdowns &&
                        dataKey.includes("_")
                      ) {
                        // Current breakdown segment: {metricKey}_{breakdownValue}
                        const parts = dataKey.split("_");
                        if (parts.length >= 2) {
                          const possibleMetricKey = parts[0];
                          if (selectedMetrics.some((m) => m.key === possibleMetricKey)) {
                            metricKey = possibleMetricKey;
                            breakdownValue = parts.slice(1).join("_");
                            isPrevious = false;
                          }
                        }
                      }
                      // Check if this is a previous value in compare mode (without breakdowns)
                      else if (isCompareDateEnabled && dataKey.endsWith("_previous")) {
                        metricKey = dataKey.replace("_previous", "");
                        isPrevious = true;
                      }
                      // Check if this is a breakdown segment (format: {metricKey}_{breakdownValue}) - regular mode
                      else if (isUsingBreakdowns && dataKey.includes("_")) {
                        const parts = dataKey.split("_");
                        if (parts.length >= 2) {
                          // Try to find the metric key by matching against selected metrics
                          const possibleMetricKey = parts[0];
                          if (selectedMetrics.some((m) => m.key === possibleMetricKey)) {
                            metricKey = possibleMetricKey;
                            breakdownValue = parts.slice(1).join("_");
                          }
                        }
                      }

                      // Find the metric label from metricOptions
                      const metricOption = metricOptions.find((m) => m.key === metricKey);
                      const metricLabel = metricOption?.label || metricKey;

                      // Create category label with breakdown info or previous indicator
                      let categoryLabel: string;
                      if (breakdownValue) {
                        const previousLabel = isPrevious ? " (Previous)" : "";
                        categoryLabel = `${metricLabel}${previousLabel} - ${breakdownValue}`;
                      } else if (isPrevious) {
                        categoryLabel = `${metricLabel} (Previous)`;
                      } else {
                        categoryLabel = metricLabel;
                      }

                      // Get color from categoryColors (handle both string and object format)
                      // For compare mode, use appropriate color for current/previous
                      let color: string = "teal";
                      let opacity = 1.0;

                      // For compare mode without breakdowns, always use the correct color based on isPrevious
                      if (isCompareDateEnabled && !isUsingBreakdowns) {
                        color = isPrevious ? BAR_COLORS[0] : BAR_COLORS[1];
                      } else {
                        // Get color from categoryColors (handle both string and object format)
                        const colorInfo = categoryColors.get(dataKey) as
                          | string
                          | { color: string; opacity: number }
                          | undefined;

                        if (typeof colorInfo === "string") {
                          color = colorInfo;
                        } else if (
                          colorInfo &&
                          typeof colorInfo === "object" &&
                          "color" in colorInfo
                        ) {
                          color = (colorInfo as { color: string; opacity: number }).color;
                          opacity = (colorInfo as { color: string; opacity: number })
                            .opacity;
                        }
                      }

                      // Convert to rgba if using breakdowns (has opacity) or convert color name to hex
                      let tooltipColor: string = color;
                      if (isUsingBreakdowns && opacity !== 1.0) {
                        const colorHex = getColorHex(color);
                        const hexToRgb = (hex: string) => {
                          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
                            hex,
                          );
                          return result
                            ? {
                              r: parseInt(result[1], 16),
                              g: parseInt(result[2], 16),
                              b: parseInt(result[3], 16),
                            }
                            : { r: 6, g: 182, b: 212 }; // Default cyan
                        };
                        const rgb = hexToRgb(colorHex);
                        tooltipColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
                      } else {
                        // Convert color name to hex
                        tooltipColor = getColorHex(color);
                      }

                      return {
                        category: categoryLabel,
                        value:
                          typeof payloadItem.value === "number" ? payloadItem.value : 0,
                        index: String(label ?? ""),
                        color: tooltipColor,
                        type: payloadItem.type,
                        payload: payloadItem.payload,
                        metricKey,
                      };
                    });

                    return (
                      <ChartTooltip
                        active={active}
                        payload={cleanPayload}
                        label={String(label ?? "")}
                        valueFormatter={valueFormatter}
                      />
                    );
                  }}
                />
                {isCompareDateEnabled
                  ? // Compare date mode: show current and previous bars side by side
                  selectedMetrics.flatMap((metric) => {
                    const metricKey = metric.key;
                    const labelFormatter = getLabelFormatter(metricKey);

                    if (isUsingBreakdowns) {
                      // Compare mode with breakdowns: render stacked segments for previous and current
                      const bars: JSX.Element[] = [];

                      // Render previous breakdown segments first (stacked) - appears on LEFT
                      breakdownValues.forEach((breakdownValue, breakdownIndex) => {
                        const segmentKey = `${metricKey}_previous_${breakdownValue}`;
                        const colorInfo = categoryColors.get(segmentKey) as
                          | string
                          | { color: string; opacity: number }
                          | undefined;
                        let color: string = BAR_COLORS[0]; // turquoiseLight for previous
                        let opacity = 1.0;

                        if (
                          colorInfo &&
                          typeof colorInfo === "object" &&
                          "color" in colorInfo
                        ) {
                          color = (colorInfo as { color: string; opacity: number })
                            .color;
                          opacity = (colorInfo as { color: string; opacity: number })
                            .opacity;
                        }

                        // Only show label on the top segment (last breakdown value)
                        const isTopSegment =
                          breakdownIndex === breakdownValues.length - 1;

                        // Convert color to hex and apply opacity as rgba
                        const colorHex = getColorHex(color);
                        const hexToRgb = (hex: string) => {
                          const result =
                            /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                          return result
                            ? {
                              r: parseInt(result[1], 16),
                              g: parseInt(result[2], 16),
                              b: parseInt(result[3], 16),
                            }
                            : { r: 6, g: 182, b: 212 }; // Default cyan
                        };
                        const rgb = hexToRgb(colorHex);
                        const fillColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;

                        bars.push(
                          <Bar
                            key={segmentKey}
                            dataKey={segmentKey}
                            fill={fillColor}
                            isAnimationActive={false}
                            stackId={`${metricKey}_previous`}
                            yAxisId={`yaxis-${metricKey}`}
                            radius={
                              isTopSegment
                                ? [4, 4, 0, 0] // Rounded top corners only on top segment
                                : [0, 0, 0, 0] // No rounding on middle/bottom segments
                            }
                            label={
                              isTopSegment
                                ? {
                                  position: "top",
                                  offset: 10,
                                  formatter: (
                                    value: number,
                                    payload: Record<string, unknown>,
                                  ) => {
                                    // Sum all previous segments for this metric to show total on top
                                    if (!payload) return labelFormatter(value);
                                    let total = 0;
                                    breakdownValues.forEach((bv) => {
                                      const segKey = `${metricKey}_previous_${bv}`;
                                      const segValue = payload[segKey];
                                      if (typeof segValue === "number") {
                                        total += segValue;
                                      }
                                    });
                                    return labelFormatter(total);
                                  },
                                  className:
                                    "font-Inter text-[11px] font-normal fill-brand-primary-600",
                                }
                                : false
                            }
                          />,
                        );
                      });

                      // Render current breakdown segments (stacked) - appears on RIGHT
                      breakdownValues.forEach((breakdownValue, breakdownIndex) => {
                        const segmentKey = `${metricKey}_${breakdownValue}`;
                        const colorInfo = categoryColors.get(segmentKey) as
                          | string
                          | { color: string; opacity: number }
                          | undefined;
                        let color: string = BAR_COLORS[1]; // magenta for current
                        let opacity = 1.0;

                        if (
                          colorInfo &&
                          typeof colorInfo === "object" &&
                          "color" in colorInfo
                        ) {
                          color = (colorInfo as { color: string; opacity: number })
                            .color;
                          opacity = (colorInfo as { color: string; opacity: number })
                            .opacity;
                        }

                        // Only show label on the top segment (last breakdown value)
                        const isTopSegment =
                          breakdownIndex === breakdownValues.length - 1;

                        // Convert color to hex and apply opacity as rgba
                        const colorHex = getColorHex(color);
                        const hexToRgb = (hex: string) => {
                          const result =
                            /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                          return result
                            ? {
                              r: parseInt(result[1], 16),
                              g: parseInt(result[2], 16),
                              b: parseInt(result[3], 16),
                            }
                            : { r: 6, g: 182, b: 212 }; // Default cyan
                        };
                        const rgb = hexToRgb(colorHex);
                        const fillColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;

                        bars.push(
                          <Bar
                            key={segmentKey}
                            dataKey={segmentKey}
                            fill={fillColor}
                            isAnimationActive={false}
                            stackId={`${metricKey}_current`}
                            yAxisId={`yaxis-${metricKey}`}
                            radius={
                              isTopSegment
                                ? [4, 4, 0, 0] // Rounded top corners only on top segment
                                : [0, 0, 0, 0] // No rounding on middle/bottom segments
                            }
                            label={
                              isTopSegment
                                ? {
                                  position: "top",
                                  offset: 10,
                                  formatter: (
                                    value: number,
                                    payload: Record<string, unknown>,
                                  ) => {
                                    // Sum all current segments for this metric to show total on top
                                    if (!payload) return labelFormatter(value);
                                    let total = 0;
                                    breakdownValues.forEach((bv) => {
                                      const segKey = `${metricKey}_${bv}`;
                                      const segValue = payload[segKey];
                                      if (typeof segValue === "number") {
                                        total += segValue;
                                      }
                                    });
                                    return labelFormatter(total);
                                  },
                                  className:
                                    "font-Inter text-[11px] font-normal fill-brand-primary-600",
                                }
                                : false
                            }
                          />,
                        );
                      });

                      return bars;
                    } else {
                      // Compare mode without breakdowns: previous bar on LEFT, current bar on RIGHT
                      const previousColor = BAR_COLORS[0]; // turquoiseLight for previous
                      const currentColor = BAR_COLORS[1]; // magenta for current

                      return [
                        <Bar
                          key={`${metricKey}_previous`}
                          className={cn(getColorClassName(previousColor, "fill"))}
                          dataKey={`${metricKey}_previous`}
                          fill=""
                          isAnimationActive={false}
                          yAxisId={`yaxis-${metricKey}`}
                          radius={[4, 4, 0, 0]}
                          label={{
                            position: "top",
                            offset: 10,
                            formatter: labelFormatter,
                            className:
                              "font-Inter text-[11px] font-normal fill-brand-primary-600",
                          }}
                        />,
                        <Bar
                          key={metricKey}
                          className={cn(getColorClassName(currentColor, "fill"))}
                          dataKey={metricKey}
                          fill=""
                          isAnimationActive={false}
                          yAxisId={`yaxis-${metricKey}`}
                          radius={[4, 4, 0, 0]}
                          label={{
                            position: "top",
                            offset: 10,
                            formatter: labelFormatter,
                            className:
                              "font-Inter text-[11px] font-normal fill-brand-primary-600",
                          }}
                        />,
                      ];
                    }
                  })
                  : isUsingBreakdowns
                    ? // Render stacked bars for breakdowns
                    selectedMetrics.flatMap((metric) => {
                      const metricKey = metric.key;
                      const labelFormatter = getLabelFormatter(metricKey);

                      // Dynamic bar width based on number of metrics
                      // Reduced width with more metrics to prevent label overlap
                      const maxBarSize =
                        selectedMetrics.length === 1
                          ? 30
                          : selectedMetrics.length === 2
                            ? 24
                            : 18;

                      // Create a Bar for each breakdown segment
                      return breakdownValues.map((breakdownValue, breakdownIndex) => {
                        const segmentKey = `${metricKey}_${breakdownValue}`;
                        const colorInfo = categoryColors.get(segmentKey) as
                          | string
                          | { color: string; opacity: number }
                          | undefined;
                        let color = "cyan";
                        let opacity = 1.0;

                        if (typeof colorInfo === "string") {
                          color = colorInfo;
                        } else if (
                          colorInfo &&
                          typeof colorInfo === "object" &&
                          "color" in colorInfo
                        ) {
                          color = (colorInfo as { color: string; opacity: number })
                            .color;
                          opacity = (colorInfo as { color: string; opacity: number })
                            .opacity;
                        }

                        // Only show label on the top segment (last breakdown value)
                        const isTopSegment =
                          breakdownIndex === breakdownValues.length - 1;

                        // Convert color to hex and apply opacity as rgba
                        const colorHex = getColorHex(color);
                        // Convert hex to rgb and apply opacity
                        const hexToRgb = (hex: string) => {
                          const result =
                            /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                          return result
                            ? {
                              r: parseInt(result[1], 16),
                              g: parseInt(result[2], 16),
                              b: parseInt(result[3], 16),
                            }
                            : { r: 6, g: 182, b: 212 }; // Default cyan
                        };
                        const rgb = hexToRgb(colorHex);
                        const fillColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;

                        return (
                          <Bar
                            key={segmentKey}
                            dataKey={segmentKey}
                            fill={fillColor}
                            isAnimationActive={false}
                            stackId={metricKey} // Same stackId for all segments of the same metric
                            yAxisId={`yaxis-${metricKey}`}
                            radius={
                              isTopSegment
                                ? [4, 4, 0, 0] // Rounded top corners only on top segment
                                : [0, 0, 0, 0] // No rounding on middle/bottom segments
                            }
                            label={
                              isTopSegment
                                ? {
                                  position: "top",
                                  offset: 10,
                                  formatter: (
                                    value: number,
                                    payload: Record<string, unknown>,
                                  ) => {
                                    // Sum all segments for this metric to show total on top
                                    if (!payload) return labelFormatter(value);
                                    let total = 0;
                                    breakdownValues.forEach((bv) => {
                                      const segmentKey = `${metricKey}_${bv}`;
                                      const segmentValue = payload[segmentKey];
                                      if (typeof segmentValue === "number") {
                                        total += segmentValue;
                                      }
                                    });
                                    return labelFormatter(total);
                                  },
                                  className:
                                    "font-Inter text-[11px] font-normal fill-brand-primary-600",
                                }
                                : false
                            }
                          />
                        );
                      });
                    })
                    : // Original behavior for non-breakdown charts
                    selectedMetrics.map((metric, index) => {
                      const metricKey = metric.key;
                      const color = BAR_COLORS[index % BAR_COLORS.length];
                      const labelFormatter = getLabelFormatter(metricKey);

                      // Dynamic bar width based on number of metrics
                      // Reduced width with more metrics to prevent label overlap
                      const maxBarSize =
                        selectedMetrics.length === 1
                          ? 30
                          : selectedMetrics.length === 2
                            ? 24
                            : 18;

                      return (
                        <Bar
                          key={metricKey}
                          className={cn(getColorClassName(color, "fill"))}
                          dataKey={metricKey}
                          fill=""
                          isAnimationActive={false}
                          yAxisId={`yaxis-${metricKey}`}
                          radius={[4, 4, 0, 0]} // Rounded top corners: [topLeft, topRight, bottomRight, bottomLeft]
                          maxBarSize={maxBarSize}
                          label={{
                            position: "top",
                            offset: 10,
                            formatter: labelFormatter,
                            className:
                              "font-Inter text-[11px] font-normal fill-brand-primary-600",
                          }}
                        />
                      );
                    })}
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
      <FooterInfo
        description={
          isCompareDateEnabled
            ? "Bar charts support comparing one metric at a time."
            : "This chart uses only the visible table for clearer comparison."
        }
        backgroundColor="#E7F3FF"
        textColor="#0071D8"
        iconBgColor="white"
        iconColor="#2A94F4"
        borderColor="#C6E3FF"
        buttonText="Okay, got it"
        buttonIconColor="#FF6A33"
        showButton={true}
        storageKey={
          isCompareDateEnabled ? COMPARE_DATE_FOOTER_DISMISSED_KEY : FOOTER_DISMISSED_KEY
        }
      />
      {previewCreative && (
        <Dialog
          open={!!previewCreative}
          onOpenChange={(open) => !open && setPreviewCreative(undefined)}
        >
          <DialogContent
            hideCloseButton
            className="max-w-sm p-0 overflow-visible bg-transparent border-0"
          >
            {(() => {
              const creativeObj = previewCreative.creative as {
                videos?: Array<{
                  permalink_url?: string;
                  source?: string;
                  thumbnail_url?: string;
                }>;
                images?: Array<{
                  permalink_url?: string;
                  url?: string;
                  uncompressed_url?: string;
                }>;
                thumbnail_url?: string;
                account_icon_url?: string;
                account_name?: string;
              };

              const hasVideo = creativeObj.videos && creativeObj.videos.length > 0;
              const mediaSource = hasVideo
                ? creativeObj.videos?.[0]?.permalink_url ||
                creativeObj.videos?.[0]?.source
                : creativeObj.images?.[0]?.permalink_url || creativeObj.images?.[0]?.url;
              const mediaType = hasVideo ? "video" : "image";
              const thumbnail =
                creativeObj.thumbnail_url ||
                creativeObj.videos?.[0]?.thumbnail_url ||
                creativeObj.images?.[0]?.permalink_url;

              return (
                <AdCard
                  className="!max-h-[90vh] w-auto overflow-hidden"
                  header={{
                    icon: creativeObj.account_icon_url,
                    title: previewCreative.adName,
                  }}
                  body={{
                    media: {
                      source: mediaSource || "",
                      type: mediaType,
                      thumbnail: thumbnail,
                    },
                  }}
                />
              );
            })()}
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
});
